/**
 * Weather model and SOURCE selector. Like the tidal-stream sources, the app can
 * show one weather source at a time so students can compare them on the same
 * instant. The first source is SYNTHETIC: a deterministic "typical UK year" of
 * wind over the training area, so a scenario can be set up where wind opposes
 * tide at a headland without depending on a live feed (and without pretending
 * today's real Biscay weather belongs on a fictional chart in a 2019 tide year).
 *
 * Later sources slot in behind the same interface:
 *  - 'enav'  : the day-or-two of wind the RYA eNav site carries (captured like the
 *              tidal data), reduced to training content.
 *  - 'gfs' / 'ecmwf' : real GRIB-derived wind for REAL-AREA use with the phone
 *              GPS, pulled (as JSON, no in-browser GRIB decode) when online.
 *
 * The model is intentionally smooth and reproducible: same instant + position
 * always gives the same wind, so the chart, the barbs, and the (later) auto-router
 * all read one consistent field. Synthetic, for training; not a real forecast.
 */
import { TIDAL_DIAMONDS } from '@/tides';
import { WEATHER_MODELS, CONSENSUS_MODEL, type RealModelId } from './models';

/**
 * A weather source is either the synthetic training field or one of the real
 * models, named by its Open-Meteo id. It used to be three short names with a
 * lookup table onto those ids, and the gap between the two vocabularies is what
 * broke the overlay: a catalogue id was cast into this type, the table did not
 * know it, and the request went out asking for `models=undefined`. One set of
 * ids now, so the compiler catches that rather than the skipper.
 */
export type WeatherSourceId = 'synthetic' | RealModelId;

export interface WeatherSourceMeta {
  id: WeatherSourceId;
  name: string;
  character: string;
  /** True for the real-world models — usable only on a real-area (Antares) chart. */
  realArea: boolean;
}

/**
 * Selectable weather sources. 'synthetic' is the fictional training field; the
 * rest are real models (Open-Meteo), which only apply on a real-area chart.
 */
export const WEATHER_SOURCES: WeatherSourceMeta[] = [
  {
    id: 'synthetic',
    name: 'Training (synthetic)',
    character: 'Typical UK year · the fictional area',
    realArea: false,
  },
  // Built from the model catalogue rather than listed again here, so adding a
  // model in one place adds it everywhere and the two can never drift apart.
  // The blend is added explicitly because it is not in that catalogue: it is not
  // a model and cannot be fetched as one.
  ...[CONSENSUS_MODEL, ...WEATHER_MODELS].map((m) => ({
    id: m.id,
    name: m.name,
    character: `${m.provider} · ${m.resolutionKm[0] === m.resolutionKm[1] ? `${m.resolutionKm[0]} km` : `${m.resolutionKm[0]} to ${m.resolutionKm[1]} km`} · real-area charts only`,
    realArea: true,
  })),
];

/** True for the real-world model sources (as opposed to the synthetic field). */
export function isRealSource(id: WeatherSourceId): boolean {
  return id !== 'synthetic';
}

export interface WindSample {
  /** [lon, lat]. */
  position: [number, number];
  /** Direction the wind comes FROM, degrees true. */
  dirFromDeg: number;
  /** Wind speed, knots. */
  speedKn: number;
}

const DAY_MS = 86_400_000;

/** Cheap deterministic hash → 0..1. */
function hash01(n: number): number {
  const s = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
}

/** Smooth (value-noise) interpolation of the hash over a continuous input. */
function smooth(x: number): number {
  const i = Math.floor(x);
  const f = x - i;
  const a = hash01(i);
  const b = hash01(i + 1);
  const t = f * f * (3 - 2 * f);
  return a + (b - a) * t; // 0..1
}

function deg(n: number): number {
  return ((n % 360) + 360) % 360;
}

/**
 * Synthetic wind at a point and instant. Prevailing south-westerly (the UK norm),
 * wandering over days like a passing synoptic pattern, with a mild diurnal wobble
 * and gentle spatial variation. Deterministic: same inputs, same wind.
 */
function syntheticWind(ms: number, lon: number, lat: number): { dirFromDeg: number; speedKn: number } {
  const days = ms / DAY_MS;
  const hour = (ms / 3_600_000) % 24;

  // Synoptic wander over a few days: direction swings around SW (225°), speed
  // rises and falls through the Beaufort range, the odd blow up towards a gale.
  const dirWander = (smooth(days * 0.6) - 0.5) * 2 * 90; // ±90° over days
  const speedBase = 6 + smooth(days * 0.8 + 4) * 18; // ~6..24 kn
  const gust = Math.max(0, smooth(days * 0.8 + 4) - 0.78) * 60; // occasional surge to ~30+

  // Diurnal nudge (a touch more breeze through the afternoon).
  const diurnal = Math.sin(((hour - 3) / 24) * 2 * Math.PI) * 1.5;

  // Spatial variation so the field is not flat: small, smooth offsets by position.
  const dirLocal = (smooth(lon * 6.3 + lat * 4.1) - 0.5) * 2 * 18; // ±18°
  const speedLocal = (smooth(lat * 7.7 - lon * 5.2 + 20) - 0.5) * 2 * 3; // ±3 kn

  const dirFromDeg = deg(225 + dirWander + dirLocal);
  const speedKn = Math.max(0, speedBase + gust + diurnal + speedLocal);
  return { dirFromDeg, speedKn: Math.round(speedKn * 10) / 10 };
}

/**
 * The SYNTHETIC wind at a point/instant. The real sources (GFS/ECMWF/ICON) come
 * over the network (see realWeather + the real-weather store), so this only ever
 * returns the synthetic training field; `source` is ignored, kept for call-site
 * symmetry (the chartwork and auto-router run on the synthetic wind).
 */
export function windAt(_source: WeatherSourceId, ms: number, position: [number, number]): WindSample {
  const w = syntheticWind(ms, position[0], position[1]);
  return { position, dirFromDeg: w.dirFromDeg, speedKn: w.speedKn };
}

/** Bounding box of the training area, derived from the charted tidal diamonds. */
function trainingBounds(): { minLon: number; minLat: number; maxLon: number; maxLat: number } {
  let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;
  for (const d of TIDAL_DIAMONDS) {
    const [lon, lat] = d.position;
    if (lon < minLon) minLon = lon;
    if (lat < minLat) minLat = lat;
    if (lon > maxLon) maxLon = lon;
    if (lat > maxLat) maxLat = lat;
  }
  if (!Number.isFinite(minLon)) {
    // No diamonds loaded: fall back to the default training centre.
    return { minLon: -6.4, minLat: 45.7, maxLon: -5.4, maxLat: 46.4 };
  }
  // Pad so barbs cover a little beyond the diamond cluster.
  const padLon = (maxLon - minLon) * 0.15 + 0.05;
  const padLat = (maxLat - minLat) * 0.15 + 0.05;
  return { minLon: minLon - padLon, minLat: minLat - padLat, maxLon: maxLon + padLon, maxLat: maxLat + padLat };
}

/**
 * A grid of wind samples across the training area for the barb overlay. Cols/rows
 * keep the field readable rather than dense. Same instant → same field.
 */
export function windFieldAt(source: WeatherSourceId, ms: number, cols = 9, rows = 7): WindSample[] {
  const b = trainingBounds();
  const out: WindSample[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const lon = b.minLon + ((b.maxLon - b.minLon) * c) / (cols - 1);
      const lat = b.minLat + ((b.maxLat - b.minLat) * r) / (rows - 1);
      out.push(windAt(source, ms, [lon, lat]));
    }
  }
  return out;
}

export * from './seaState';
export * from './forecastLog';
export * from './grib';

// Multi-model forecasting: the catalogue, the several-models table, and what
// they agree on. See src/weather/consensus.ts for why nothing is deleted.
export * from './models';
export * from './consensus';
export * from './multiModel';
